import { FloraButton } from '@grupoboticario/flora-react';
import {
  ArrowsCircleIcon,
  BoxIcon,
  CheckSingleIcon,
  CheckDoubleIcon,
  SmileCircleIcon,
  TruckIcon,
  CrossIcon,
} from '@grupoboticario/flora-react-icons';
import { useParams } from 'react-router-dom';

import { ResponsiveFloraTypography } from 'components';
import config from 'config';
import { BRANDS } from 'config/environments/shared';
import { FeatureFlag } from 'domains/feature-flag.d';
import { getShortDate } from 'helpers';
import { useFeatureFlag, useOrderDetail } from 'hooks';

import { StatusItemProps, TrackingInfoProps } from './Tracking.components';
import Styles from './Tracking.components.styles';

import type { Tracking } from 'domains/order';

export const StatusItem = ({ status = 'capture' }: StatusItemProps) => {
  const { id = '0' } = useParams<{ id: string }>();
  const { data: order } = useOrderDetail(id);
  const isBot = BRANDS.BOT === config.app.slugname;
  const tagStatus = order?.status;

  const { value: isTrackingButtonEnabled } = useFeatureFlag(
    FeatureFlag.SHOW_TRACKING_BUTTON,
    false
  );

  if (!order?.tracking.length) {
    return null;
  }

  const trackingInfo: { [key: string]: TrackingInfoProps } = {
    capture: {
      icon: <CheckSingleIcon />,
      order: 1,
      title: 'Pedido realizado',
    },
    paymentProcess: {
      icon: <ArrowsCircleIcon />,
      order: 2,
      title: 'Processando pagamento',
    },
    processing: {
      icon: <CheckDoubleIcon />,
      order: 3,
      title: 'Pagamento aprovado',
    },
    picking: {
      icon: <BoxIcon />,
      order: 4,
      title: 'Pedido em separação',
    },
    delivering: {
      title: 'Pedido em transporte',
      order: 5,
      icon: <TruckIcon />,
    },
    delivered: {
      title: 'Pedido entregue',
      order: 6,
      icon: <SmileCircleIcon />,
    },
    canceled: {
      title: 'Cancelado',
      order: 7,
      icon: <CrossIcon color='$statusErrorBackgroundAlt' />,
    },
  };

  const statusTrackDetails = order?.tracking.filter((item) => item.status === status) || [];
  const lastStatus = order?.tracking.findLast(({ status: lastTrackStatus }) =>
    Object.keys(trackingInfo).includes(lastTrackStatus)
  ).status as Tracking['status'];

  const tracking =
    statusTrackDetails[statusTrackDetails.length > 0 ? statusTrackDetails.length - 1 : 0];
  const isTracking = trackingInfo[status].order === trackingInfo['delivering'].order;
  const isDeliveringStatus = trackingInfo[status].order === trackingInfo['delivering'].order;
  const showTrackingButton = isTracking && isDeliveringStatus && isBot && isTrackingButtonEnabled;
  const isComplete = trackingInfo[lastStatus].order > trackingInfo[status].order;
  const isActive = tracking ? lastStatus === status : false;

  return (
    <Styles.Wrapper>
      <Styles.Status>
        <Styles.IconWrapper
          data-active={isActive}
          data-complete={isComplete}
          data-canceled={tagStatus === 'canceled' && status === 'canceled'}
        >
          {trackingInfo[status].icon}
        </Styles.IconWrapper>
        <Styles.Line
          data-active={isActive}
          data-complete={isComplete}
          data-line
          data-tracking={isTracking}
        />
      </Styles.Status>
      <Styles.InfoWrapper
        data-active={isActive}
        data-complete={isComplete}
        data-tracking={isTracking}
      >
        <ResponsiveFloraTypography
          fontSize='bodyLargeShort'
          fontWeight={isActive ? 'bold' : 'medium'}
          aria-label={
            !isComplete && !isActive
              ? `${trackingInfo[status].title}: não iniciado.`
              : `${trackingInfo[status].title}`
          }
        >
          {trackingInfo[status].title}
        </ResponsiveFloraTypography>
        {Boolean(tracking) && (
          <ResponsiveFloraTypography
            fontSize='bodyLargeShort'
            aria-hidden={!isComplete && !isActive}
          >
            {getShortDate(tracking.closingDate || tracking.date)}
          </ResponsiveFloraTypography>
        )}
        {order?.trackingUrl && showTrackingButton && (
          <Styles.TrackingButton>
            <FloraButton
              size='small'
              as='a'
              href={order.trackingUrl}
              target='_blank'
              rel='noopener noreferrer'
              autotagging-flora='on'
              data-flora='button'
              data-flora-text='rastrear-pedido'
            >
              <ResponsiveFloraTypography fontWeight='bold'>
                Rastrear pedido
              </ResponsiveFloraTypography>
            </FloraButton>
          </Styles.TrackingButton>
        )}
      </Styles.InfoWrapper>
    </Styles.Wrapper>
  );
};
